import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

const baseUrl = process.env.REACT_APP_SECONDARY_SERVER;

export const pathwayAdminApi = createApi({
  reducerPath: 'pathwayAdminApi',
  baseQuery: fetchBaseQuery({
    baseUrl,
    prepareHeaders: (headers) => {
      const token = localStorage.getItem('AdminAccessToken');
      if (token) {
        headers.set('authorization', `Bearer ${token}`);
      }
      return headers;
    },
  }),
  tagTypes: ['Category', 'Phase', 'Step', 'Exercise', 'Field', 'Analytics'],
  endpoints: (builder) => ({
    // Category endpoints
    getCategories: builder.query({
      query: () => 'api/admin/pathways/categories',
      providesTags: ['Category'],
    }),
    createCategory: builder.mutation({
      query: (data) => ({
        url: 'api/admin/pathways/categories',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Category'],
    }),
    updateCategory: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `api/admin/pathways/categories/${id}`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Category'],
    }),
    deleteCategory: builder.mutation({
      query: (id) => ({
        url: `api/admin/pathways/categories/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Category'],
    }),
    publishCategory: builder.mutation({
      query: (id) => ({
        url: `api/admin/pathways/categories/${id}/publish`,
        method: 'PATCH',
      }),
      invalidatesTags: ['Category'],
    }),
    reorderCategories: builder.mutation({
      query: (data) => ({
        url: 'api/admin/pathways/categories/reorder',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Category'],
    }),

    // Phase endpoints
    getPhases: builder.query({
      query: (categoryId) => ({
        url: 'api/admin/pathways/phases',
        params: { category_id: categoryId },
      }),
      providesTags: ['Phase'],
    }),
    createPhase: builder.mutation({
      query: (data) => ({
        url: 'api/admin/pathways/phases',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Phase', 'Category'],
    }),
    updatePhase: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `api/admin/pathways/phases/${id}`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Phase', 'Category'],
    }),
    deletePhase: builder.mutation({
      query: (id) => ({
        url: `api/admin/pathways/phases/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Phase', 'Category'],
    }),
    reorderPhases: builder.mutation({
      query: (data) => ({
        url: 'api/admin/pathways/phases/reorder',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Phase'],
    }),

    // Step endpoints
    getSteps: builder.query({
      query: (phaseId) => ({
        url: 'api/admin/pathways/steps',
        params: { phase_id: phaseId },
      }),
      providesTags: ['Step'],
    }),
    getStepDetails: builder.query({
      query: (id) => `api/admin/pathways/steps/${id}`,
      providesTags: ['Step', 'Exercise', 'Field'],
    }),
    createStep: builder.mutation({
      query: (data) => ({
        url: 'api/admin/pathways/steps',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Step', 'Phase'],
    }),
    updateStep: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `api/admin/pathways/steps/${id}`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Step', 'Phase'],
    }),
    deleteStep: builder.mutation({
      query: (id) => ({
        url: `api/admin/pathways/steps/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Step', 'Phase'],
    }),
    reorderSteps: builder.mutation({
      query: (data) => ({
        url: 'api/admin/pathways/steps/reorder',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Step'],
    }),

    // Exercise endpoints
    getExercises: builder.query({
      query: (stepId) => ({
        url: 'api/admin/pathways/exercises',
        params: { step_id: stepId },
      }),
      providesTags: ['Exercise'],
    }),
    createExercise: builder.mutation({
      query: (data) => ({
        url: 'api/admin/pathways/exercises',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Exercise', 'Step'],
    }),
    updateExercise: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `api/admin/pathways/exercises/${id}`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Exercise', 'Step'],
    }),
    deleteExercise: builder.mutation({
      query: (id) => ({
        url: `api/admin/pathways/exercises/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Exercise', 'Step'],
    }),
    reorderExercises: builder.mutation({
      query: (data) => ({
        url: 'api/admin/pathways/exercises/reorder',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Exercise'],
    }),

    // Field endpoints
    getFields: builder.query({
      query: (exerciseId) => ({
        url: 'api/admin/pathways/fields',
        params: { exercise_id: exerciseId },
      }),
      providesTags: ['Field'],
    }),
    createField: builder.mutation({
      query: (data) => ({
        url: 'api/admin/pathways/fields',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Field', 'Exercise'],
    }),
    updateField: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `api/admin/pathways/fields/${id}`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Field', 'Exercise'],
    }),
    deleteField: builder.mutation({
      query: (id) => ({
        url: `api/admin/pathways/fields/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Field', 'Exercise'],
    }),
    reorderFields: builder.mutation({
      query: (data) => ({
        url: 'api/admin/pathways/fields/reorder',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Field'],
    }),
    getAvailableSourceFields: builder.query({
      query: (params) => ({
        url: 'api/admin/pathways/fields/available-sources',
        params,
      }),
      providesTags: ['Field'],
    }),
    getFieldTypes: builder.query({
      query: () => 'api/admin/pathways/fields/types',
      providesTags: ['Field'],
    }),
    getMetadataSchema: builder.query({
      query: (componentType) => `api/admin/pathways/fields/types/${componentType}/schema`,
      providesTags: ['Field'],
    }),
    validateFieldConfiguration: builder.mutation({
      query: (data) => ({
        url: 'api/admin/pathways/fields/validate',
        method: 'POST',
        body: data,
      }),
    }),

    // Analytics endpoints
    getAnalytics: builder.query({
      query: () => 'api/admin/pathways/analytics/overview',
      providesTags: ['Analytics'],
    }),
    getStepAnalytics: builder.query({
      query: (stepId) => `api/admin/pathways/analytics/step/${stepId}`,
      providesTags: ['Analytics'],
    }),
    getUserProgressList: builder.query({
      query: () => 'api/admin/pathways/analytics/users',
      providesTags: ['Analytics'],
    }),
  }),
});

export const {
  useGetCategoriesQuery,
  useCreateCategoryMutation,
  useUpdateCategoryMutation,
  useDeleteCategoryMutation,
  usePublishCategoryMutation,
  useReorderCategoriesMutation,
  useGetPhasesQuery,
  useCreatePhaseMutation,
  useUpdatePhaseMutation,
  useDeletePhaseMutation,
  useReorderPhasesMutation,
  useGetStepsQuery,
  useGetStepDetailsQuery,
  useCreateStepMutation,
  useUpdateStepMutation,
  useDeleteStepMutation,
  useReorderStepsMutation,
  useGetExercisesQuery,
  useCreateExerciseMutation,
  useUpdateExerciseMutation,
  useDeleteExerciseMutation,
  useReorderExercisesMutation,
  useGetFieldsQuery,
  useCreateFieldMutation,
  useUpdateFieldMutation,
  useDeleteFieldMutation,
  useReorderFieldsMutation,
  useGetAvailableSourceFieldsQuery,
  useGetFieldTypesQuery,
  useGetMetadataSchemaQuery,
  useValidateFieldConfigurationMutation,
  useGetAnalyticsQuery,
  useGetStepAnalyticsQuery,
  useGetUserProgressListQuery,
} = pathwayAdminApi;
